import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import {
  type PopperPlacementType,
  type PopperProps,
} from '@mui/material/Popper';
import { FocusTrap } from '@mui/base/FocusTrap';
import { useTheme } from '../../../theme';
import {
  useEffect,
  useMemo,
  useState,
  type AriaRole,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { PopoverArrow } from './popoverArrow';

export type PopperPaddingType =
  | string
  | {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };

export type PopoverProps = {
  id?: string;
  children: ReactNode;
  anchorElement?: Element | null;
  openerElement?: Element | null;
  placement?: PopperPlacementType;
  isOpen?: boolean;
  onOpen?: (openerId?: string) => void;
  onClose?: () => void;
  hasArrow?: boolean;
  arrowSize?: number;
  /**
   * Displacement away from the anchor element (along the arrow)
   */
  offset?: number;
  /**
   * Displacement along the anchor element (perpendicular to the arrow)
   */
  skidding?: number;
  flipPadding?: PopperPaddingType;
  closesOnClickAway?: boolean;
  closesOnEscape?: boolean;
  trapsFocus?: boolean;
  disableRestoreFocus?: boolean;
  role?: AriaRole;
  style?: CSSProperties;
  containerCss?: CSSProperties;
  className?: string;
  patientName?: string;
  preventScrollOnFocus?: boolean;
  disablePortal?: boolean;
};

export function Popover({
  id,
  children,
  anchorElement,
  openerElement = anchorElement,
  placement = 'auto',
  isOpen = true,
  onOpen,
  onClose = (): void => undefined,
  hasArrow = false,
  arrowSize = 12,
  offset = 0,
  skidding = 0,
  flipPadding,
  closesOnClickAway = true,
  closesOnEscape = true,
  trapsFocus = true,
  role = 'presentation',
  style,
  className,
  preventScrollOnFocus = false,
  disablePortal = false,
  containerCss,
}: PopoverProps): ReactElement {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [arrow, setArrow] = useState<Element | null>(null);
  const [arrowColor, setArrowColor] = useState('');
  const theme = useTheme();

  useEffect(() => {
    if (container) {
      const containerStyles = getComputedStyle(container);
      setArrowColor(containerStyles.backgroundColor);

      if (preventScrollOnFocus) {
        container.focus({ preventScroll: true });
      }
    }
  }, [container, preventScrollOnFocus]);

  useEffect(() => {
    if (isOpen) {
      onOpen?.(id);
    }
  }, [isOpen]);

  const popperModifiers = useMemo<PopperProps['modifiers']>(
    () => [
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          rootBoundary: 'document',
          altBoundary: true,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [
            skidding,
            offset + (hasArrow ? arrowSize - 1 : 0), // Adding -1 for non-retina displays that show an extra pixel
          ],
        },
      },
      {
        name: 'arrow',
        enabled: hasArrow,
        options: {
          element: arrow,
        },
      },
      ...(Boolean(flipPadding)
        ? [
            {
              name: 'flip',
              enabled: true,
              options: {
                padding: flipPadding,
              },
            },
          ]
        : []),
    ],
    [arrow, arrowSize, hasArrow, offset, skidding, flipPadding],
  );

  function onClickAway(event: UIEvent): void {
    let target = event.target as Node;

    // If target is a child element of an SVG (like rect, path, etc.),
    // find the nearest parent SVG element
    if (target instanceof Element && event instanceof MouseEvent) {
      const tagName = target.tagName?.toLowerCase();
      if (
        tagName &&
        [
          'rect',
          'path',
          'circle',
          'ellipse',
          'line',
          'polyline',
          'polygon',
          'text',
          'tspan',
          'g',
          'defs',
          'use',
        ].includes(tagName)
      ) {
        const path = event.composedPath();
        for (const element of path) {
          if (
            element instanceof Element &&
            element.tagName?.toLowerCase() === 'svg'
          ) {
            target = element;
            break;
          }
        }
      }
    }

    const clickedOnOpener = openerElement?.contains(target) ?? false;
    if (closesOnClickAway && !clickedOnOpener) {
      onClose();
    }
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (closesOnEscape && event.key === 'Escape') {
      onClose();
    }
  }

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Popper
        id={id}
        open={isOpen}
        placement={placement}
        anchorEl={anchorElement}
        modifiers={popperModifiers}
        onKeyDown={onKeyDown}
        role={role}
        sx={{ zIndex: theme.zIndex.popover, ...containerCss }}
        disablePortal={disablePortal}
      >
        {hasArrow && (
          <PopoverArrow ref={setArrow} size={arrowSize} color={arrowColor} />
        )}
        <FocusTrap
          open
          disableEnforceFocus={!trapsFocus}
          disableAutoFocus={preventScrollOnFocus}
        >
          <div
            ref={setContainer}
            tabIndex={-1}
            style={style}
            className={className}
            css={{ outline: 0 }}
          >
            {children}
          </div>
        </FocusTrap>
      </Popper>
    </ClickAwayListener>
  );
}
